// src/components/TrialBanner.tsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuth } from '@/auth/AuthProvider';
import { resolveAccess } from '@/lib/subscriptionStatus';
import type { UserSubscription } from '@/lib/subscriptionStatus';

const DISMISSED_KEY = 'trialBannerDismissed';

export default function TrialBanner() {
  const { user, claims } = useAuth();
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [isTrialing, setIsTrialing] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    // Skip for privileged accounts — they won't have trialing status
    if (claims?.isPro || claims?.demo || claims?.auditBypassBilling) return;
    if (!user?.uid) return;

    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;

      const data = snap.data() as Partial<UserSubscription> & Record<string, unknown>;
      if (!data.subscriptionStatus) return;

      const result = resolveAccess({
        subscriptionStatus: data.subscriptionStatus as UserSubscription['subscriptionStatus'],
        trialStartedAt: data.trialStartedAt as UserSubscription['trialStartedAt'],
        trialEndsAt: data.trialEndsAt as UserSubscription['trialEndsAt'],
      });

      setIsTrialing(result.isTrialing);
      setDaysRemaining(result.daysRemaining);
    });

    return unsub;
  }, [user?.uid, claims]);

  if (!isTrialing || daysRemaining === null) return null;

  // Always visible (non-dismissible) when 3 or fewer days remain
  const isUrgent = daysRemaining <= 3;

  // Hidden entirely when more than 3 days remain and user dismissed it
  if (!isUrgent && dismissed) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* sessionStorage unavailable */
    }
    setDismissed(true);
  };

  return (
    <div
      className={`flex items-center justify-between gap-4 px-4 py-2 text-sm font-medium text-white ${
        isUrgent ? 'bg-red-600' : 'bg-brand-orange'
      }`}
      role="banner"
      aria-label="Free trial status"
    >
      <span>
        {isUrgent
          ? `⚠ Your free trial ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} — subscribe to keep access.`
          : `Your free trial ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}.`}
      </span>

      <div className="flex shrink-0 items-center gap-3">
        <Link
          to="/subscribe"
          className="rounded-md bg-white px-3 py-1 text-sm font-semibold text-brand-navy transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-white/50"
        >
          Subscribe Now
        </Link>

        {!isUrgent && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss trial banner"
            className="rounded p-0.5 opacity-80 transition hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
